import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link } from "gatsby";
import { AnchorLink } from "gatsby-plugin-anchor-links";
import ExternalIcon from "../images/external.inline.svg";

const StyledExternal = styled.a`
  padding: 9px 25px 7px 25px;
`;
const StyledLink = styled(Link)`
  padding: 1rem 40px;
`;
const StyledAnchor = styled(AnchorLink)`
  padding: 10px 25px 10px 25px;
`;
const ButtonBase = styled(({ to, target, rel, children, ...props }) =>
  to?.includes("tel:") || to?.includes("mailto:") ? (
    <StyledExternal href={to} target={target} rel={rel} {...props}>
      {children}
    </StyledExternal>
  ) : target ? (
    <StyledExternal href={to} target={target} rel={rel} {...props}>
      {children} <ExternalIcon />
    </StyledExternal>
  ) : to?.includes("#") ? (
    <StyledAnchor to={to} {...props}>
      {children}
    </StyledAnchor>
  ) : (
    <StyledLink to={to} {...props}>
      {children}
    </StyledLink>
  )
)`
  font-size: 1rem;
  line-height: initial;
  text-decoration: none!important;
  transition: 0.3s all ease-in-out;
  font-family:   font-family: "Poppins", sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 300px;
  margin: 5px 10px 5px 10px;
  &:first-child {
    margin-left: 0;
  }
  &:hover {
    text-decoration: none;
    cursor: pointer;
  }
  @media (max-width: 1024px) {
    width: 100%;
    margin-left: 0;
  }
`;
const Primary = styled(({ ...rest }) => <ButtonBase {...rest} />)`
  background-color: ${({ backgroundColor }) =>
    backgroundColor || "#F55951"}!important;
  color: ${({ textColor }) => textColor || "#fff"}!important;
  border: 1px solid
    ${({ backgroundColor }) => backgroundColor || "#F55951"}!important;
  &:hover {
    filter: brightness(120%);
    span {
      filter: brightness(90%);
    }
  }
`;
const Secondary = styled(({ ...rest }) => <ButtonBase {...rest} />)`
  border: 1px solid ${({ textColor }) => textColor || "#F55951"}!important;
  background-color: "transparent";
  color: ${({ textColor }) => textColor || "#F55951"}!important;
  &:hover {
    background-color: ${({ backgroundColor }) =>
      backgroundColor || "#F55951"}!important;
    border: 1px solid ${({ textColor }) => textColor || "#fff"}!important;
    color: ${(props) => props.textColor || "#fff"}!important;
  }
`;

export default function Button({
  url,
  variant,
  label,
  innerClassName,
  target,
  rel,
  backgroundColor,
  textColor,
  hasIcon,
  children,
}) {
  if (variant === "primary") {
    return (
      <Primary
        aria-label={label}
        to={url}
        target={target}
        rel={rel}
        className={innerClassName}
        backgroundColor={backgroundColor}
        textColor={textColor}
      >
        <span>{hasIcon ? children : label}</span>
      </Primary>
    );
  } else if (variant === "secondary") {
    return (
      <Secondary
        aria-label={label}
        to={url}
        target={target}
        rel={rel}
        className={innerClassName}
        backgroundColor={backgroundColor}
        textColor={textColor}
      >
        {hasIcon ? children : label}
      </Secondary>
    );
  }
}

Button.propTypes = {
  url: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["primary", "secondary"]),
  label: PropTypes.string.isRequired,
  innerClassName: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  hasIcon: PropTypes.bool,
  children: PropTypes.object,
};

Button.defaultProps = {
  innerClassName: undefined,
  variant: "primary",
  hasIcon: false,
};
